/**
 * WhatsAppLogo — logo canónico de WhatsApp (burbuja + auricular) con dos
 * regiones de color independientes.
 *
 * Diferencia respecto al `WhatsAppGlyph` de trazo que vivía inline en
 * `FloatingWhatsApp`: este es FILL-based y expone cada región como un `<path>`
 * separado con su propio `className`. Eso permite re-colorear la burbuja
 * (originalmente verde) y el auricular (originalmente blanco) usando clases
 * utilitarias del Design System (`fill-primary-700`, `fill-cream-50`,
 * `fill-gold-500`, etc.) sin necesidad de svgr ni dependencias nuevas.
 *
 * Trazado SVG: paths estándar del logo (viewBox 32×32). La burbuja conserva
 * la cola triangular abajo-izquierda; el auricular usa la curva estilizada
 * canónica centrada dentro de la burbuja.
 *
 * Por defecto, sin props extra, queda:
 *   burbuja  → hereda `currentColor` (fill-current)
 *   auricular → `fill-cream-50`
 *
 * El consumidor (p. ej. FloatingWhatsApp) sobreescribe ambas clases para
 * pintar el universo Ceciluz Arte (morado / crema / dorado).
 */
import { type SVGProps } from 'react';
import { cn } from '../../lib/cn';

export interface WhatsAppLogoProps extends Omit<SVGProps<SVGSVGElement>, 'fill'> {
  /**
   * Clase para el fill de la burbuja (la región que en el logo original
   * es verde). Default: `fill-current` — hereda el color del padre.
   */
  bubbleClassName?: string;
  /**
   * Clase para el fill del auricular (la región que en el logo original
   * es blanco/negro). Default: `fill-cream-50`.
   */
  phoneClassName?: string;
}

export function WhatsAppLogo({
  bubbleClassName = 'fill-current',
  phoneClassName = 'fill-cream-50',
  className,
  ...rest
}: WhatsAppLogoProps) {
  return (
    <svg
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable={false}
      // `strokeLinejoin/Linecap` heredados por los paths para que el contorno
      // del logo (cuando el consumidor pase clases `stroke-*` vía
      // `bubbleClassName`) tenga esquinas suaves en vez de cortes en pico.
      strokeLinejoin="round"
      strokeLinecap="round"
      className={className}
      {...rest}
    >
      {/* Burbuja — chat bubble circular con cola triangular abajo-izquierda. */}
      <path
        className={cn(bubbleClassName)}
        d="M16 0 C7.16 0 0 7.16 0 16 c0 2.83 0.73 5.49 2.02 7.79 L0 32 l8.45 -2.21 C10.69 30.99 13.27 32 16 32 c8.84 0 16 -7.16 16 -16 S24.84 0 16 0 z"
      />
      {/* Auricular — handset estilizado centrado dentro de la burbuja. */}
      <path
        className={cn(phoneClassName)}
        d="M11.85 9.27 c -0.27 -0.6 -0.55 -0.61 -0.81 -0.62 -0.21 -0.01 -0.45 -0.01 -0.69 -0.01 -0.24 0 -0.63 0.09 -0.96 0.45 -0.33 0.36 -1.26 1.23 -1.26 3.01 0 1.77 1.29 3.48 1.47 3.72 0.18 0.24 2.49 3.99 6.15 5.43 3.04 1.2 3.66 0.96 4.32 0.9 0.66 -0.06 2.13 -0.87 2.43 -1.71 0.3 -0.84 0.3 -1.56 0.21 -1.71 -0.09 -0.15 -0.33 -0.24 -0.69 -0.42 -0.36 -0.18 -2.13 -1.05 -2.46 -1.17 -0.33 -0.12 -0.57 -0.18 -0.81 0.18 -0.24 0.36 -0.93 1.17 -1.14 1.41 -0.21 0.24 -0.42 0.27 -0.78 0.09 -0.36 -0.18 -1.52 -0.56 -2.89 -1.78 -1.07 -0.95 -1.79 -2.13 -2 -2.49 -0.21 -0.36 -0.02 -0.55 0.16 -0.73 0.16 -0.16 0.36 -0.42 0.54 -0.63 0.18 -0.21 0.24 -0.36 0.36 -0.6 0.12 -0.24 0.06 -0.45 -0.03 -0.63 -0.09 -0.18 -0.78 -1.95 -1.07 -2.66 z"
      />
    </svg>
  );
}
